/**
 * PATH: packages/drafting/src/canvas/geometry.ts
 * FILE_ID: DRAFTING-CANVAS-GEOMETRY-001
 * PURPOSE: Pure geometry for the canvas — paper sizes, grid lines, the
 *   scale bar, and zoom-to-fit. No React/Konva here (f(input)->output).
 */

import type { PaperSize, IrUnit } from '../ir/schema'
import { realWorldLength, formatLength } from '../ir/normalize'

export interface Size {
  w: number
  h: number
}

/** Canvas view transform: drawing-unit -> screen px is `p*scale + {x,y}`. */
export interface View {
  scale: number
  x: number
  y: number
}

/** clampScale — keep zoom inside sane bounds so the drawing can't vanish. */
export function clampScale(scale: number, min = 0.05, max = 40): number {
  if (!Number.isFinite(scale)) return min
  return Math.min(max, Math.max(min, scale))
}

/**
 * zoomToPoint — zoom by `factor` while keeping the drawing point currently
 * under `pointer` (screen coords) pinned in place. Pure: f(view,pt,factor)
 * -> view. This is the standard "zoom where the cursor is" transform.
 */
export function zoomToPoint(
  view: View,
  pointer: { x: number; y: number },
  factor: number,
  min?: number,
  max?: number,
): View {
  const newScale = clampScale(view.scale * factor, min, max)
  const f = newScale / view.scale
  return {
    scale: newScale,
    x: pointer.x - (pointer.x - view.x) * f,
    y: pointer.y - (pointer.y - view.y) * f,
  }
}

/** fitView — a View that fits `content` centred inside `viewport`. */
export function fitView(content: Size, viewport: Size, padding = 24): View {
  const scale = fitScale(content, viewport, padding)
  return {
    scale,
    x: Math.max((viewport.w - content.w * scale) / 2, padding),
    y: Math.max((viewport.h - content.h * scale) / 2, padding),
  }
}

/** ISO A-series in millimetres, landscape (longer side = width). */
const A_SERIES: Record<string, Size> = {
  A0: { w: 1189, h: 841 },
  A1: { w: 841, h: 594 },
  A2: { w: 594, h: 420 },
  A3: { w: 420, h: 297 },
  A4: { w: 297, h: 210 },
}

/** paperSizeMm — resolve a PaperSize to {w,h} millimetres. */
export function paperSizeMm(paper: PaperSize): Size {
  if (typeof paper === 'object') return { w: paper.w, h: paper.h }
  return A_SERIES[paper] ?? A_SERIES.A3
}

export interface GridLine {
  points: [number, number, number, number]
}

/**
 * buildGridLines — grid line segments across a paper of `size` (drawing
 * units), spaced every `step`. Caps the count so a tiny step on a big
 * sheet can't emit tens of thousands of lines and stall the canvas.
 */
export function buildGridLines(size: Size, step: number, maxLines = 400): GridLine[] {
  if (!Number.isFinite(step) || step <= 0) return []
  const lines: GridLine[] = []
  const cols = Math.floor(size.w / step)
  const rows = Math.floor(size.h / step)
  if (cols + rows > maxLines) return []
  for (let x = 0; x <= size.w + 1e-6; x += step) {
    lines.push({ points: [x, 0, x, size.h] })
  }
  for (let y = 0; y <= size.h + 1e-6; y += step) {
    lines.push({ points: [0, y, size.w, y] })
  }
  return lines
}

/** fitScale — px-per-unit that fits `content` into `viewport` with padding. */
export function fitScale(content: Size, viewport: Size, padding = 24): number {
  const availW = Math.max(viewport.w - padding * 2, 1)
  const availH = Math.max(viewport.h - padding * 2, 1)
  const scale = Math.min(availW / content.w, availH / content.h)
  return Number.isFinite(scale) && scale > 0 ? scale : 1
}

export interface ScaleBarSpec {
  /** length of the bar in drawing units */
  drawingLength: number
  /** human label, e.g. "100 mm" */
  label: string
}

/**
 * scaleBarSpec — pick a "nice" real-world length whose drawn size is a
 * comfortable on-screen width, and report both the drawing-unit length
 * (to draw the bar) and the label (to print under it).
 */
export function scaleBarSpec(
  ratio: string,
  unit: IrUnit,
  pxPerUnit: number,
  targetPx = 120,
): ScaleBarSpec | null {
  // target drawing-unit length for ~targetPx on screen
  const targetDrawing = targetPx / Math.max(pxPerUnit, 1e-6)
  const real = realWorldLength(targetDrawing, ratio)
  if (real == null) return null
  // round real length to a 1/2/5 × 10^n "nice" number
  const mag = 10 ** Math.floor(Math.log10(Math.max(real, 1e-6)))
  const norm = real / mag
  const niceNorm = norm >= 5 ? 5 : norm >= 2 ? 2 : 1
  const niceReal = niceNorm * mag
  const back = realWorldLength(1, ratio) // real per 1 drawing unit
  const drawingLength = back ? niceReal / back : targetDrawing
  return { drawingLength, label: formatLength(niceReal, unit) }
}
